/**
 * @sazeo/sdk — Documents hooks (real implementation)
 *
 * Hooks that call the `documents` Edge Function. All requests carry the
 * caller's Supabase JWT — RLS enforces tenant isolation. No service_role
 * ever reaches the browser.
 *
 * Hook signatures are deliberately minimal (no `supabase`/`tenantId`
 * arguments) so the existing tenant-app UI keeps compiling. The Supabase
 * client is resolved on demand through `@sazeo/supabase-tenant`.
 *
 * Upload flow:
 *   1. POST /documents/upload-url   → { upload_url, storage_key, document_id }
 *   2. Client PUTs the binary to `upload_url`
 *   3. On failure, DELETE /documents/:id reverses the orphan metadata row
 *
 * Paired spec: specs/CODEX_PHASE2_WAVE3_DOCUMENTS_SEARCH.md §2.3
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Row types — compatible with the UI's existing imports
// ---------------------------------------------------------------------------

export interface FolderRow {
  id: string;
  tenant_id: string;
  parent_id: string | null;
  name: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface FolderTreeNode extends FolderRow {
  children: FolderTreeNode[];
}

export interface DocumentRow {
  id: string;
  tenant_id: string;
  folder_id: string | null;
  name: string;
  mime_type: string;
  size_bytes: number;
  storage_key: string;
  checksum_sha256: string | null;
  uploaded_by: string;
  uploaded_by_name?: string | null;
  description: string | null;
  source_lang: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export type DocumentSharePermission = 'view' | 'edit';

export interface DocumentShareRow {
  id: string;
  document_id: string;
  shared_with_user_id: string | null;
  shared_with_team_id: string | null;
  shared_with_name?: string | null;
  permission: DocumentSharePermission;
  created_by: string;
  created_at: string;
}

export interface UploadDocumentInput {
  file: File;
  folder_id: string | null;
}

// ---------------------------------------------------------------------------
// Tree builder (kept exported — used by DocumentsPage)
// ---------------------------------------------------------------------------

/**
 * Build a recursive tree from a flat list of folders (parent_id references).
 * Root nodes = parent_id === null. Children are sorted alphabetically by name.
 */
export function buildFolderTree(rows: readonly FolderRow[]): FolderTreeNode[] {
  const byId = new Map<string, FolderTreeNode>();
  for (const row of rows) {
    byId.set(row.id, { ...row, children: [] });
  }
  const roots: FolderTreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parent_id && byId.has(node.parent_id)) {
      byId.get(node.parent_id)?.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRecursive = (list: FolderTreeNode[]) => {
    list.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of list) sortRecursive(n.children);
  };
  sortRecursive(roots);
  return roots;
}

// ---------------------------------------------------------------------------
// Internal fetch helpers
// ---------------------------------------------------------------------------

/**
 * Minimal subset of the Supabase client we actually touch. Declared here so
 * this module does not hard-depend on the full Supabase TS surface.
 */
interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(): SupabaseAuthLike {
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    // In SSR / prerender environments where env vars are missing, fall back
    // to a no-op stub so hooks can still mount without throwing.
    return {
      auth: {
        getSession: async () => ({ data: { session: null } }),
      },
    };
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const client = resolveClient();
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

async function apiDelete(path: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, { method: 'DELETE', headers });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
}

// ---------------------------------------------------------------------------
// Minimal query/mutation primitives
// ---------------------------------------------------------------------------
// We avoid hard-depending on @tanstack/react-query here because the existing
// UI expects a tighter, simpler shape ({ data, isLoading, error, refetch }).

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps is caller-controlled
  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (mountedRef.current) setData(d);
      })
      .catch((e) => {
        if (mountedRef.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
  }, deps);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps is caller-controlled
  useEffect(() => {
    run();
  }, deps);

  return { data, isLoading, error, refetch: run };
}

// ---------------------------------------------------------------------------
// Folder hooks
// ---------------------------------------------------------------------------

/** Flat list of folders; components call `buildFolderTree` to render. */
export function useFolderTree() {
  return useAsyncQuery<FolderRow[]>(() => apiFetch<FolderRow[]>('documents/folders'), [], []);
}

/** Single folder row. */
export function useFolder(folderId: string | null) {
  return useAsyncQuery<FolderRow | null>(
    () => (folderId ? apiFetch<FolderRow>(`documents/folders/${folderId}`) : Promise.resolve(null)),
    null,
    [folderId],
    Boolean(folderId),
  );
}

export function useCreateFolder() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mutate = useCallback(async (input: { parent_id: string | null; name: string }) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<FolderRow>('documents/folders', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending, error, setError };
}

export function useRenameFolder() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(
    async (input: { folder_id: string; name?: string; parent_id?: string | null }) => {
      setIsPending(true);
      try {
        const body: Record<string, unknown> = {};
        if (input.name !== undefined) body.name = input.name;
        if (input.parent_id !== undefined) body.parent_id = input.parent_id;
        return await apiFetch<FolderRow>(`documents/folders/${input.folder_id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } finally {
        setIsPending(false);
      }
    },
    [],
  );
  return { mutate, isPending };
}

export function useDeleteFolder() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (folderId: string) => {
    setIsPending(true);
    try {
      await apiDelete(`documents/folders/${folderId}`);
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

// ---------------------------------------------------------------------------
// Document list + single
// ---------------------------------------------------------------------------

/** Documents in the given folder (or root if null). */
export function useDocuments(folderId: string | null) {
  return useAsyncQuery<DocumentRow[]>(
    () => {
      const qs = folderId ? `?folder_id=${encodeURIComponent(folderId)}` : '?folder_id=root';
      return apiFetch<DocumentRow[]>(`documents${qs}`);
    },
    [],
    [folderId],
  );
}

export function useDocument(documentId: string | null) {
  return useAsyncQuery<DocumentRow | null>(
    () => (documentId ? apiFetch<DocumentRow>(`documents/${documentId}`) : Promise.resolve(null)),
    null,
    [documentId],
    Boolean(documentId),
  );
}

// ---------------------------------------------------------------------------
// Upload (signed URL round-trip)
// ---------------------------------------------------------------------------

interface UploadUrlResponse {
  upload_url: string;
  storage_key: string;
  document_id: string;
  expires_at: string;
  max_bytes: number;
}

export function useUploadDocument() {
  const [isPending, setIsPending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (input: UploadDocumentInput): Promise<DocumentRow | null> => {
    setIsPending(true);
    setProgress(0);
    setError(null);
    try {
      const meta = {
        name: input.file.name,
        mime_type: input.file.type || 'application/octet-stream',
        size_bytes: input.file.size,
        folder_id: input.folder_id,
      };

      const signed = await apiFetch<UploadUrlResponse>('documents/upload-url', {
        method: 'POST',
        body: JSON.stringify(meta),
      });

      const putRes = await fetch(signed.upload_url, {
        method: 'PUT',
        headers: { 'content-type': meta.mime_type },
        body: input.file,
      });
      setProgress(90);

      if (!putRes.ok) {
        // Orphan cleanup — keep the index consistent.
        try {
          await apiDelete(`documents/${signed.document_id}`);
        } catch {
          // Swallow; propagate the original error.
        }
        throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`);
      }

      const doc = await apiFetch<DocumentRow>(`documents/${signed.document_id}`);
      setProgress(100);
      return doc;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      return null;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutate, isPending, progress, error };
}

export function useDownloadDocument() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUrl = useCallback(async (documentId: string): Promise<string | null> => {
    setIsPending(true);
    setError(null);
    try {
      const res = await apiFetch<{ url: string; expires_at: string }>(
        `documents/${documentId}/download-url`,
      );
      return res.url;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      return null;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { fetchUrl, isPending, error };
}

export function useDownloadDocumentVersion() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUrl = useCallback(
    async (documentId: string, versionId: string): Promise<string | null> => {
      setIsPending(true);
      setError(null);
      try {
        const res = await apiFetch<{ url: string; expires_at: string }>(
          `documents/${documentId}/versions/${versionId}/download-url`,
        );
        return res.url;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      } finally {
        setIsPending(false);
      }
    },
    [],
  );

  return { fetchUrl, isPending, error };
}

// ---------------------------------------------------------------------------
// Update / delete
// ---------------------------------------------------------------------------

export function useUpdateDocument() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(
    async (input: {
      document_id: string;
      name?: string;
      folder_id?: string | null;
      description?: string | null;
    }) => {
      setIsPending(true);
      try {
        const body: Record<string, unknown> = {};
        if (input.name !== undefined) body.name = input.name;
        if (input.folder_id !== undefined) body.folder_id = input.folder_id;
        if (input.description !== undefined) body.description = input.description;
        return await apiFetch<DocumentRow>(`documents/${input.document_id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } finally {
        setIsPending(false);
      }
    },
    [],
  );
  return { mutate, isPending };
}

export function useDeleteDocument() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (documentId: string) => {
    setIsPending(true);
    try {
      await apiDelete(`documents/${documentId}`);
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

// ---------------------------------------------------------------------------
// Shares
// ---------------------------------------------------------------------------

export function useDocumentShares(documentId: string | null) {
  return useAsyncQuery<DocumentShareRow[]>(
    () =>
      documentId
        ? apiFetch<DocumentShareRow[]>(`documents/${documentId}/shares`)
        : Promise.resolve([]),
    [],
    [documentId],
    Boolean(documentId),
  );
}

export function useShareDocument() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(
    async (input: {
      document_id: string;
      shared_with_user_id?: string;
      shared_with_team_id?: string;
      permission: DocumentSharePermission;
    }) => {
      setIsPending(true);
      try {
        const body: Record<string, unknown> = {
          permission: input.permission,
        };
        if (input.shared_with_user_id) body.user_id = input.shared_with_user_id;
        if (input.shared_with_team_id) body.team_id = input.shared_with_team_id;
        return await apiFetch<DocumentShareRow>(`documents/${input.document_id}/shares`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
      } finally {
        setIsPending(false);
      }
    },
    [],
  );
  return { mutate, isPending };
}

export function useUnshareDocument() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (input: { document_id: string; share_id: string }) => {
    setIsPending(true);
    try {
      await apiDelete(`documents/${input.document_id}/shares/${input.share_id}`);
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

// ---------------------------------------------------------------------------
// Document Versions (L86 — version history & restore)
// ---------------------------------------------------------------------------

export interface DocumentVersionRow {
  id: string;
  document_id: string;
  version_number: number;
  file_path: string;
  file_size_bytes: number | null;
  change_summary: string | null;
  created_by: string | null;
  created_at: string;
}

/** Retrieve version history for a document. */
export function useDocumentVersions(documentId: string | null) {
  return useAsyncQuery<DocumentVersionRow[]>(
    () =>
      documentId
        ? apiFetch<DocumentVersionRow[]>(`documents/${documentId}/versions`)
        : Promise.resolve([]),
    [],
    [documentId],
    Boolean(documentId),
  );
}

/** Upload a new version of a document. Increments version number. */
export function useUploadDocumentVersion() {
  const [isPending, setIsPending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      document_id: string;
      file: File;
      change_summary?: string;
    }): Promise<DocumentRow | null> => {
      setIsPending(true);
      setProgress(0);
      setError(null);
      try {
        const meta = {
          mime_type: input.file.type || 'application/octet-stream',
          size_bytes: input.file.size,
          change_summary: input.change_summary,
        };

        const signed = await apiFetch<UploadUrlResponse>(
          `documents/${input.document_id}/upload-version`,
          {
            method: 'POST',
            body: JSON.stringify(meta),
          },
        );

        const putRes = await fetch(signed.upload_url, {
          method: 'PUT',
          headers: { 'content-type': meta.mime_type },
          body: input.file,
        });
        setProgress(90);

        if (!putRes.ok) {
          throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`);
        }

        const doc = await apiFetch<DocumentRow>(`documents/${input.document_id}`);
        setProgress(100);
        return doc;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        return null;
      } finally {
        setIsPending(false);
      }
    },
    [],
  );

  return { mutate, isPending, progress, error };
}

/** Restore a document to a previous version. Creates new version pointing to old file. */
export function useRestoreDocumentVersion() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { document_id: string; version_number: number }): Promise<DocumentRow | null> => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<DocumentRow>(`documents/${input.document_id}/restore-version`, {
          method: 'POST',
          body: JSON.stringify({ version_number: input.version_number }),
        });
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        return null;
      } finally {
        setIsPending(false);
      }
    },
    [],
  );

  return { mutate, isPending, error };
}

// ---------------------------------------------------------------------------
// Granular Share Control (L86 — role/department sharing)
// ---------------------------------------------------------------------------

export interface GranularShareRow extends DocumentShareRow {
  shared_with_role?: string | null;
  shared_with_department_id?: string | null;
}

/** Share document with user, role, or department + set permission. */
export function useShareDocumentGranular() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(
    async (input: {
      document_id: string;
      target: 'user' | 'role' | 'department';
      target_id: string;
      permission: 'view' | 'download' | 'edit' | 'delete';
    }) => {
      setIsPending(true);
      try {
        const body: Record<string, unknown> = { permission: input.permission };
        if (input.target === 'user') body.shared_with_user_id = input.target_id;
        else if (input.target === 'role') body.shared_with_role = input.target_id;
        else if (input.target === 'department') body.shared_with_department_id = input.target_id;

        return await apiFetch<GranularShareRow>(`documents/${input.document_id}/shares`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
      } finally {
        setIsPending(false);
      }
    },
    [],
  );
  return { mutate, isPending };
}

/** Update permission on an existing share. */
export function useUpdateDocumentSharePermission() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(
    async (input: {
      share_id: string;
      permission: 'view' | 'download' | 'edit' | 'delete';
    }) => {
      setIsPending(true);
      try {
        return await apiFetch<GranularShareRow>(`documents/shares/${input.share_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ permission: input.permission }),
        });
      } finally {
        setIsPending(false);
      }
    },
    [],
  );
  return { mutate, isPending };
}
